import { User } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/common/ui/card";
import { Skeleton } from "@/common/ui/skeleton";

import type { WhoopBodyMeasurement, WhoopProfile } from "../devices-types";

interface ProfileCardProps {
  profile: WhoopProfile | null;
  body: WhoopBodyMeasurement | null;
  isLoading: boolean;
}

function metersToFeetInches(meters: number): string {
  const totalInches = Math.round(meters * 39.3701);
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches % 12;
  return `${feet}'${inches}"`;
}

function kgToLbs(kg: number): string {
  return `${Math.round(kg * 2.20462)} lbs`;
}

export function ProfileCard({ profile, body, isLoading }: ProfileCardProps): React.ReactElement {
  return (
    <Card data-testid="profile-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Profile</CardTitle>
        <User className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : profile ? (
          <div className="space-y-3">
            <div>
              <div data-testid="profile-name" className="text-lg font-bold">
                {profile.first_name} {profile.last_name}
              </div>
              <div data-testid="profile-email" className="text-sm text-muted-foreground">
                {profile.email}
              </div>
            </div>
            {body && (
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Height</span>
                  <div data-testid="profile-height" className="font-medium">
                    {metersToFeetInches(body.height_meter)}
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">Weight</span>
                  <div data-testid="profile-weight" className="font-medium">
                    {kgToLbs(body.weight_kilogram)}
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">Max HR</span>
                  <div data-testid="profile-maxhr" className="font-medium">
                    {body.max_heart_rate} bpm
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No profile data available</p>
        )}
      </CardContent>
    </Card>
  );
}
